import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
	getIntegrations,
	getIntegration,
	saveIntegration,
	importIntegrationExcel,
	getIntegrationPurchase,
	getIntegrationUsageLookups,
	getIntegrationUsage,
	saveIntegrationParams,
	resendIntegration,
	pendingIntegration,
	syncOrders,
	syncOrdersDelivery,
	syncOrdersSale,
	syncDataFile,
} from "@/api/Integrations/integration-api";

export const key = "integrations";

export function useIntegrations(filters) {
	return useQuery([key, "list", filters], () => getIntegrations(filters));
}

export function useIntegration(id_element, enabled) {
	return useQuery([key, "detail", id_element], () => getIntegration(id_element), {
		enabled: enabled,
		keepPreviousData: true,
	});
}

export function useSaveIntegration() {
	const queryClient = useQueryClient();

	return useMutation(saveIntegration, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;
			const detail = response.detail;

			queryClient.setQueryData([key, "detail", filters, detail.id], detail);
			queryClient.setQueryData([key, "list", filters], list);
		},
	});
}

export function useImportIntegrationExcel() {
	return useMutation(importIntegrationExcel, {
		onSuccess: (_data) => {},
	});
}

export function useIntegrationPurchases(filters, enabled) {
	return useQuery([key, "list-purchase", filters], () => getIntegrationPurchase(filters), {
		enabled: enabled,
	});
}

export function usePendingPurchase() {
	const queryClient = useQueryClient();

	return useMutation(pendingIntegration, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;

			queryClient.setQueryData([key, "list-purchase", filters], list);
		},
	});
}

// Usage
export function useIntegrationUsagesLookups() {
	return useQuery([key, "list-usage-lookups"], () => getIntegrationUsageLookups());
}

export function useIntegrationUsages(filters, enabled) {
	return useQuery([key, "list-usage", filters], () => getIntegrationUsage(filters), {
		enabled: enabled,
	});
}

export function useSaveIntegrationParams() {
	const queryClient = useQueryClient();

	return useMutation(saveIntegrationParams, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;

			queryClient.setQueryData([key, "list-usage", filters], list);
		},
	});
}

export function useResendIntegration() {
	const queryClient = useQueryClient();

	return useMutation(resendIntegration, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;

			queryClient.setQueryData([key, "list-usage", filters], list);
		},
	});
}

const SYNC_STEPS = {
	sapbo: ["orders", "delivery", "sale"],
	nisira: ["orders", "delivery"],
	agri: ["orders", "delivery", "sale"],
	softland: ["orders"],
	softland_connector: ["orders"],
	odoo: ["orders"],
};

const STEP_API = {
	orders: syncOrders,
	delivery: syncOrdersDelivery,
	sale: syncOrdersSale,
};

const STEP_ERROR_MESSAGES = {
	orders: "Ha ocurrido un error al sincronizar las ordenes de compra.",
	delivery: "Ha ocurrido un error al sincronizar las guías de despacho.",
	sale: "Ha ocurrido un error al sincronizar las facturas.",
};

const INITIAL_SYNC_STATE = {
	orders: { done: false, message: "" },
	delivery: { done: false, message: "" },
	sale: { done: false, message: "" },
	finished: false,
};

export function useSyncIntegration() {
	const [syncState, setSyncState] = useState(INITIAL_SYNC_STATE);

	const { mutate: runSync } = useMutation(
		async ({ integration, file }) => {
			setSyncState(INITIAL_SYNC_STATE);

			if (file) {
				await syncDataFile({ integration, file });
				const steps = SYNC_STEPS[integration] ?? [];
				const doneSteps = Object.fromEntries(steps.map((step) => [step, { done: true, message: "" }]));
				setSyncState((prev) => ({ ...prev, ...doneSteps }));
				await new Promise((resolve) => setTimeout(resolve, 2000));
				return;
			}

			const steps = SYNC_STEPS[integration] ?? [];
			for (const step of steps) {
				try {
					const response = await STEP_API[step](integration);
					const message = response?.message ? STEP_ERROR_MESSAGES[step] : "";
					setSyncState((prev) => ({ ...prev, [step]: { done: true, message } }));
				} catch {
					setSyncState((prev) => ({ ...prev, [step]: { done: true, message: STEP_ERROR_MESSAGES[step] } }));
				}
			}
		},
		{
			onSuccess: () => setSyncState((prev) => ({ ...prev, finished: true })),
			onError: () => {
				toast.error("Ha ocurrido un error al sincronizar.");
				setSyncState((prev) => ({ ...prev, finished: true }));
			},
		},
	);

	return { syncState, runSync };
}
